import api from './api';

/**
 * Review Service - CMS Backend API Integration
 * Base URL: /api/cms/reviews
 *
 * All endpoints require JWT Bearer token authentication.
 * All responses follow ApiResponse<T> wrapper pattern:
 * { success: boolean, message: string, data: T | null, timestamp: string }
 */

const REVIEWS_URL = '/api/admin/reviews';

const reviewService = {
  /**
   * Get all reviews with pagination and sorting
   * @param {Object} params - Query parameters
   * @param {number} [params.page=0] - Page number (0-indexed)
   * @param {number} [params.size=20] - Items per page
   * @param {string} [params.sort='createdAt,desc'] - Sort field and direction
   * @returns {Promise<{success: boolean, message: string, data: PaginatedResponse<Review>}>}
   */
  getReviews: (params = {}) => {
    const { page = 0, size = 20, sort = 'createdAt,desc' } = params;
    return api.get(REVIEWS_URL, {
      params: { page, size, sort }
    });
  },

  /**
   * Get pending reviews only (sorted oldest first for fair processing)
   * @param {Object} params - Query parameters
   * @param {number} [params.page=0] - Page number (0-indexed)
   * @param {number} [params.size=20] - Items per page
   * @returns {Promise<{success: boolean, message: string, data: PaginatedResponse<Review>}>}
   */
  getPendingReviews: (params = {}) => {
    const { page = 0, size = 20 } = params;
    return api.get(`${REVIEWS_URL}/pending`, {
      params: { page, size }
    });
  },

  /**
   * Get single review by ID
   * @param {number} id - Review ID
   * @returns {Promise<{success: boolean, message: string, data: Review}>}
   */
  getReview: (id) => {
    return api.get(`${REVIEWS_URL}/${id}`);
  },

  /**
   * Get review statistics by status
   * @returns {Promise<{success: boolean, message: string, data: ReviewStats}>}
   * ReviewStats: { total: number, PENDING: number, APPROVED: number, REJECTED: number, FLAGGED: number }
   */
  getReviewStats: () => {
    return api.get(`${REVIEWS_URL}/stats`);
  },

  /**
   * Approve a pending or flagged review
   * Makes the review visible on the ecommerce site
   * @param {number} id - Review ID
   * @returns {Promise<{success: boolean, message: string, data: Review}>}
   */
  approveReview: (id) => {
    return api.post(`${REVIEWS_URL}/${id}/approve`);
  },

  /**
   * Reject a review with a reason
   * Review will not be shown on the ecommerce site
   * @param {number} id - Review ID
   * @param {string} reason - Rejection reason (3-500 characters)
   * @returns {Promise<{success: boolean, message: string, data: Review}>}
   */
  rejectReview: (id, reason) => {
    return api.post(`${REVIEWS_URL}/${id}/reject`, { reason });
  },

  /**
   * Flag a review for escalation/supervisor review
   * @param {number} id - Review ID
   * @param {string} reason - Flag reason (3-500 characters)
   * @returns {Promise<{success: boolean, message: string, data: Review}>}
   */
  flagReview: (id, reason) => {
    return api.post(`${REVIEWS_URL}/${id}/flag`, { reason });
  },

  /**
   * Unpublish a previously approved review
   * Removes the review from the ecommerce site
   * @param {number} id - Review ID
   * @param {string} reason - Unpublish reason (3-500 characters)
   * @returns {Promise<{success: boolean, message: string, data: Review}>}
   */
  unpublishReview: (id, reason) => {
    return api.post(`${REVIEWS_URL}/${id}/unpublish`, { reason });
  },
};

export default reviewService;
